import { forwardRef, useMemo } from 'react';
import { cx } from 'class-variance-authority';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import * as styles from './styles';
import type { StyleProps } from './styles';
import { pipe } from 'fp-ts/lib/function';
import { fromNullable, getOrElseW, map } from 'fp-ts/lib/Option';
import {
  CheckmarkFilled,
  Error,
  HourglassTop,
  Warning,
} from '@oresundsbron/icons';

export const strMatcher =
  <T extends string, R>(adts: Record<T, () => R>) =>
  (v: T) =>
    adts[v]();

type TagTypeMap = BaseTypeMap<'span', StyleProps & { icon?: boolean }>;

export type TagProps = ComponentProps<TagTypeMap>;

const svgClassName = 'mr-1';

export const Tag: Component<TagTypeMap> = forwardRef(function Tag(
  { children, className, as = 'span', intent, size, icon = false, ...rest },
  ref
) {
  const iconComponent = useMemo(
    () =>
      pipe(
        intent,
        fromNullable,
        map(
          strMatcher({
            positive: () => <CheckmarkFilled className={svgClassName} />,
            negative: () => <Error className={svgClassName} />,
            warning: () => <Warning className={svgClassName} />,
            neutral: () => <HourglassTop className={svgClassName} />,
          })
        ),
        getOrElseW(() => <HourglassTop />)
      ),
    [intent]
  );

  return (
    <Box
      className={cx(styles.tag({ intent, size }), className)}
      ref={ref}
      as={as}
      {...rest}
    >
      {icon && iconComponent}
      {children}
    </Box>
  );
});
